import type { GetCurrentClient } from '@cloudFunctions/types/clients/getCurrentClient';
import type { SendClientMagicLink } from '@cloudFunctions/types/clients/sendClientMagicLink';
import type { SendClientPasswordResetLink } from '@cloudFunctions/types/clients/sendClientPasswordResetLink';
import type { BackendRouteDefinition } from '../types';
import { withIdempotencyKeyHeader } from '../../../../../shared/backend/routes/idempotency';

// Migrated to the `api` transport by S6-WB3 (PRA-796). The legacy callable
// exports remain deployed until S9-WB2, so an older clients-app build still
// pointing at them keeps working until that flip.
//
// Path convention: single `:` here (e.g. `auth:current`) — `buildRoutePath`
// in `shared/backend/transports/httpRequest.ts` only treats a colon as a
// path param when it follows `/` or `^`, so an inline `:current` is a
// literal. The corresponding api-core-node Fastify route registers the
// double-colon form (`auth::current`) because Fastify's own router treats
// any `:` as a param marker and `::` as the escape; the two together match
// the literal URL clients send.
//
// The two send-email routes derive a stable idempotency key from the caller's
// inputs so rapid double-clicks deduplicate inside the throttle TTL. The key
// uses the raw (un-normalized) email so the body fingerprint computed by the
// api-core-node middleware matches on a repeat click — normalizing the key
// without normalizing the body would surface as a 409 fingerprint-mismatch
// for two clicks that visually look identical.

const buildSendEmailIdempotencyKey = (props: {
  scope: 'magic-link' | 'password-reset';
  firmHandle: string;
  emailAddress: string;
}): string => {
  return `clients-${props.scope}::${props.firmHandle}::${props.emailAddress}`;
};

export const clientAuthRoutes = {
  getCurrentClient: {
    transport: 'api',
    method: 'GET',
    path: '/clients/auth:current',
    query: ({ environment }: GetCurrentClient.RequestData) => ({ environment }),
  },
  sendClientMagicLink: {
    transport: 'api',
    method: 'POST',
    path: '/public/clients/auth:send-magic-link',
    body: ({ emailAddress, firmHandle }: SendClientMagicLink.Props) => ({
      emailAddress,
      firmHandle,
    }),
    headers: ({ emailAddress, firmHandle }: SendClientMagicLink.Props) =>
      withIdempotencyKeyHeader({
        idempotencyKey: buildSendEmailIdempotencyKey({
          scope: 'magic-link',
          firmHandle,
          emailAddress,
        }),
      }),
  },
  sendClientPasswordResetLink: {
    transport: 'api',
    method: 'POST',
    path: '/public/clients/auth:send-password-reset-link',
    body: ({
      emailAddress,
      firmHandle,
    }: SendClientPasswordResetLink.Props) => ({
      emailAddress,
      firmHandle,
    }),
    headers: ({
      emailAddress,
      firmHandle,
    }: SendClientPasswordResetLink.Props) =>
      withIdempotencyKeyHeader({
        idempotencyKey: buildSendEmailIdempotencyKey({
          scope: 'password-reset',
          firmHandle,
          emailAddress,
        }),
      }),
  },
} as const satisfies Record<string, BackendRouteDefinition>;
